import type { QueryClient } from "@tanstack/react-query";
import { invalidateNotifications } from "web_myplann/Api/Notifications/notificatioKeys";
import { openQuestionnaireFromPush } from "web_myplann/Api/Nsure/openQuestionnaire";
import { patchNotificationsIdReadV2 } from "web_myplann/Infrastructure/Api/Api";
import { handleCall } from "web_myplann/Infrastructure/Api/ApiUtils";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import {
	fallbackToNotificationList,
	getDestinationRoute,
	isInvestmentQuestionnaireDestination,
	isNoDeepLinkGroup,
	isReminderDestination,
	isWebDestination,
	type NavigateFn,
	navigateToSource,
} from "web_myplann/Utils/WebViewHandlers/NotificationDestinations";
import {
	getMessageId,
	isDuplicate,
	sendReceipt,
} from "web_myplann/Utils/WebViewHandlers/NotificationReceipt";
import {
	type NavigateNotificationRequest,
	parseNotificationId,
} from "web_myplann/Utils/WebViewModel";

const markNotificationRead = (id: number, queryClient: QueryClient) => {
	void handleCall(patchNotificationsIdReadV2(id), {
		skipToastFor: true,
	}).then((result) => {
		if (result.isSuccess) {
			invalidateNotifications(queryClient);
		}
	});
};

export const handleNotificationNavigation = async (
	message: NavigateNotificationRequest,
	queryClient: QueryClient,
	navigate: NavigateFn,
	getCurrentPath: () => string,
) => {
	const messageId = getMessageId(message);
	if (messageId) {
		sendReceipt(messageId);
		if (isDuplicate(messageId)) {
			return;
		}
	}

	const { payload } = message;
	const notificationId = parseNotificationId(payload.id);

	if (notificationId !== undefined) {
		markNotificationRead(notificationId, queryClient);
	}

	if (
		isInvestmentQuestionnaireDestination(payload.type, payload.notificationType)
	) {
		const opened = await openQuestionnaireFromPush(
			{
				notificationId,
				clientType: payload.clientType,
				identificationNumber: payload.identificationNumber,
			},
			queryClient,
		);
		if (!opened) {
			await navigate(AppRouting.MyPlann.Notifications.path());
		}
		return;
	}

	const route = getDestinationRoute(payload.type);
	if (route) {
		await navigateToSource(route, payload, navigate, getCurrentPath);
		return;
	}

	if (isReminderDestination(payload.type, payload.notificationType)) {
		await navigate(AppRouting.MyPlann.Notifications.path("upcoming"));
		return;
	}

	if (isWebDestination(payload.type)) {
		await fallbackToNotificationList(
			"Unhandled notification navigation type",
			payload,
			navigate,
		);
		return;
	}

	if (!payload.type || isNoDeepLinkGroup(payload.type)) {
		return;
	}
	logWarning("Notification navigation without a known destination", {
		notification_type: payload.type,
		notification_source_type: payload.notificationType,
		notification_id: payload.id,
	});
};
